// ============================================================================
// useNewAccounts — dashboard data for the all-staff "New Accounts" widget
// (Option D PR-5). Reads v_new_account_lifecycle (security_invoker → RLS-
// scoped) and groups the in-flight + recently-started accounts into the three
// lenses Bryan asks about: ⚠ Behind, Recent starts, Starting soon. Resolves
// the AM display name via the canonical v_am_name map.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { fetchAmNameMap } from '../../../shared/am-name';
import { errorMessage } from '../../../shared/errors';

export interface NewAccountRow {
  sopId: string;
  facilityId: string | null;
  displayName: string;
  marketRegion: string | null;
  amEid: string | null;
  amName: string | null;
  stage: string | null;
  plannedStartDate: string | null;
  actualStartDate: string | null;
  daysUntilStart: number | null;
  lifecycle: 'behind' | 'recent_start' | 'starting_soon';
}

export interface NewAccountsData {
  behind: NewAccountRow[];
  recent: NewAccountRow[];
  soon: NewAccountRow[];
}

interface RawRow {
  sop_id: string;
  facility_id: string | null;
  display_name: string | null;
  market_region: string | null;
  am_eid: string | null;
  stage: string | null;
  planned_start_date: string | null;
  actual_start_date: string | null;
  days_until_start: number | null;
  lifecycle: string;
}

export function useNewAccounts(enabled: boolean) {
  const [data, setData] = useState<NewAccountsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const { data: rows, error: e } = await supabase
          .from('v_new_account_lifecycle')
          .select(
            'sop_id, facility_id, display_name, market_region, am_eid, stage, planned_start_date, actual_start_date, days_until_start, lifecycle',
          )
          .in('lifecycle', ['behind', 'recent_start', 'starting_soon'])
          .order('planned_start_date', { ascending: true, nullsFirst: false });
        if (e) throw e;
        const raw = (rows ?? []) as RawRow[];
        const amMap = await fetchAmNameMap(raw.map((r) => r.am_eid));
        if (cancelled) return;

        const map = (r: RawRow): NewAccountRow => ({
          sopId: r.sop_id,
          facilityId: r.facility_id,
          displayName: r.display_name ?? r.sop_id,
          marketRegion: r.market_region,
          amEid: r.am_eid,
          amName: r.am_eid ? amMap.get(r.am_eid) ?? null : null,
          stage: r.stage,
          plannedStartDate: r.planned_start_date,
          actualStartDate: r.actual_start_date,
          daysUntilStart: r.days_until_start,
          lifecycle: r.lifecycle as NewAccountRow['lifecycle'],
        });

        const all = raw.map(map);
        setData({
          behind: all.filter((r) => r.lifecycle === 'behind'),
          recent: all.filter((r) => r.lifecycle === 'recent_start'),
          soon: all
            .filter((r) => r.lifecycle === 'starting_soon')
            .sort((a, b) => (a.daysUntilStart ?? 999) - (b.daysUntilStart ?? 999)),
        });
      } catch (err) {
        if (cancelled) return;
        console.error('[dashboard] new-accounts load failed', err);
        setError(errorMessage(err, 'Failed to load new accounts.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, tick]);

  return { data, isLoading, error, refetch };
}
